import React, { ReactElement } from 'react';
import { Component, FilterCombo, Container, HBox } from 'puzzle';
import _, { find, findIndex, orderBy } from 'lodash';
import {
  ComboBoxChangeEvent,
  ComboBoxFocusEvent,
  ComboBoxBlurEvent,
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import { BATCH, SELECTED_TM, TM_PROFILE, TMS_SERVER, TMS_UI } from "../Types";
import { TM_CONFIG } from "../../../util/Util";
import FocusControl from "./FocusControl";
import { Keys } from "@progress/kendo-react-common";


type PROPS = {
  batch: BATCH
  projectId: number
  sourceLanguage: string
  targetLanguages: string[]
  targetLanguagesData: any[]
  closeExtra: () => void
  moduleId: string
  onChange: any
  submissionMode: string
  submissionStarted: boolean,
  popupLeaveFocusEl?: ReactElement
};

type STATE = {
  tms: TMS_UI[],
  targetFormat: string
}

export class TranslationMemories extends Component<PROPS, STATE> {
  domElement: React.RefObject<HTMLDivElement> = React.createRef();
  state = {
    tms: [],
    targetFormat: ''
  };

  moduleDidMount = () => {
    const {
      moduleId,
      paClient: paClientId = undefined,
      projectId,
      sourceLanguage,
      submissionMode
    } = this.props;
    const el = this.domElement.current;
    if (el) {
      el.focus();
      el.addEventListener('keydown', this.onKeyDown);
    }

    this.dispatch('getTranslationMemories', '', {
      paClientId,
      projectId,
      sourceLanguage,
      submissionMode,
      moduleId
    });
  };

  /**
   * @inheritDoc
   */
  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any): void {
    if (!_.isEqual(prevProps, this.props) || !_.isEqual(prevState, this.state)) {
      this.props.focusControl.attachKeyDown(this.domElement.current);
    }
  }

  /**
   * @inheritDoc
   */
  componentWillUnmount() {
    super.componentWillUnmount();
    const el = this.domElement?.current;
    if (el instanceof HTMLElement) {
      el.removeEventListener('keydown', this.onKeyDown);
    }
  }

  /**
   * Adding class on focus
   */
  handleFocus = (event: ComboBoxFocusEvent | any) => {
    event.nativeEvent.target.classList?.add('k-focused')
  }

  /**
   * Remove class on blur
   */
  handleBlur = (event: ComboBoxBlurEvent | any) => {
    event.nativeEvent.target.classList?.remove('k-focused')
  }

  /**
   * Assign tm profile to each target language
   */
  mapTargetLanguages = (mappedTms: TMS_UI[], item: string): TMS_UI => {
    let result;
    const match = find(mappedTms, { targetLanguage: item });
    if (match) {
      result = match;
    } else {
      const targetLanguageData = find(this.props.targetLanguagesData, { targetLanguage: item });
      result = {
        targetLanguage: item,
        targetLanguageDisplayName: targetLanguageData.targetLanguageDisplayName,
        tmProfiles: []
      };
    }

    return result;
  };

  private onKeyDown = (e: KeyboardEvent) => {
    const popupLeaveFocusEl = this.props.popupLeaveFocusEl._element;
    if (e.key.toLowerCase() === 'e' && e.ctrlKey && e.altKey && popupLeaveFocusEl) {
      popupLeaveFocusEl.focus();
    } else if (e.keyCode === Keys.esc) {
      this.dispatch('closeExtra', '', {
        moduleId: this.props.moduleId
      });
      e.preventDefault();
      popupLeaveFocusEl.focus();
    }
  };

  /**
   * Change tmProfileId in batch
   */
  onTmChange = (event: ComboBoxChangeEvent | { [p: string]: any }, { targetLanguage }: any) => {
    const e = {
      target: {
        props: {
          targetLanguage,
          userChange: true,
          name: 'translationMemory'
        },
        value: event.value?.tmProfileId
      }
    };
    this.props.onChange(this.props.moduleId, undefined, e);
  };

  /**
   * Map server response from TMs root array to Target Language root array
   */
  reduceTms = (selectedTms: SELECTED_TM[], acc: TMS_UI[], curr: TMS_SERVER): TMS_UI[] => {
    const filteredLangs: TM_CONFIG[] = curr.languages.filter((item: TM_CONFIG) => this.props.targetLanguages.includes(item.targetLanguageId));
    filteredLangs.forEach(this.setTmLanguageData.bind(this, acc, curr, selectedTms));
    return acc;
  };

  /**
   * Set tm language data and tmProfile to each target language
   */
  setTmLanguageData = (acc: TMS_UI[], curr: TMS_SERVER, selectedTms: SELECTED_TM[], lang: TM_CONFIG) => {
    const targetLanguageData = find(this.props.targetLanguagesData, { targetLanguage: lang.targetLanguageId });
    const match: number = findIndex(acc, { targetLanguage: lang.targetLanguageId });
    const tm: TM_PROFILE = {
      isDefault: lang.default,
      targetFormat: curr.targetFormat,
      ticket: curr.ticket,
      tmLanguageDirection: lang.languageDirection,
      tmName: curr.tmName,
      tmProfileId: curr.tmProfileId,
      tmUrls: curr.tmUrls
    };
    if (match === -1) {
      acc.push({
        targetLanguage: lang.targetLanguageId,
        targetLanguageDisplayName: targetLanguageData.targetLanguageDisplayName,
        tmProfiles: [tm]
      });
    } else {
      acc[match].tmProfiles.push(tm);
    }
    this.selectTm(selectedTms, lang);
  };

  /**
   * Set batch tm if not already selected
   */
  selectTm = (selectedTms: SELECTED_TM[], lang: TM_CONFIG): void => {
    const { batch } = this.props;
    const selectedTm: any = find(selectedTms, { batchId: batch.batchId, targetLanguageId: lang.targetLanguageId });
    const language = find(batch.targetLanguageInfos, { targetLanguage: lang.targetLanguageId });
    if (!language.tmProfileId && selectedTm) {
      const e = {
        target: {
          props: {
            targetLanguage: selectedTm.targetLanguageId,
            name: 'translationMemory'
          },
          value: selectedTm.id
        }
      };
      this.props.onChange(this.props.moduleId, undefined, e);
    }
  };

  /**
   * Map server response from TMs root array to Target Language root array
   * and set the result into local state
   */
  setTM = (tms: TMS_SERVER[] = [], selectedTms: SELECTED_TM[] = [], targetFormat: string): void => {
    let mappedTms = tms.reduce(this.reduceTms.bind(this, selectedTms), []);
    const data = this.props.targetLanguages.map(this.mapTargetLanguages.bind(this, mappedTms));
    const sortedTms = orderBy(data, ['targetLanguageDisplayName'], ['asc']);
    this.setState({ tms: sortedTms, targetFormat });
  };

  /**
   * Set TM profile value
   */
  setTmProfileValue = ({ targetLanguage, tmProfiles }: TMS_UI) => {
    const tms = tmProfiles.filter((tmProfile: any) => this.state.targetFormat === tmProfile.targetFormat);
    let value: undefined | TM_PROFILE = undefined;
    const { batch } = this.props;
    const language = find(batch.targetLanguageInfos, { targetLanguage });

    language && (value = find(tms, { tmProfileId: language.tmProfileId }));
    !value && (value = find(tms, { isDefault: true }));

    return value;
  };

  /**
   * TM dropdown item custom renderer
   */
  tmItemRenderer = (li: ReactElement<any>, itemProps: ListItemProps): React.ReactNode => {
    let className: string = li.props.className;
    let disabled: boolean = this.state.targetFormat !== itemProps.dataItem.targetFormat;

    itemProps.dataItem.isDefault && (className = className.concat(' tp-default-language'));
    disabled && (className = className.concat(' k-state-disabled'));

    return React.cloneElement(
      li,
      { ...li.props, className, disabled },
      [...li.props.children]
    );
  };

  render() {
    return (
      <div
        ref={this.domElement}
        className={'tp-custom-popup-wrap tp-tms'}
        tabIndex={0}
        onKeyDown={(e) => this.props.focusControl.preserveFocusOnPopup(this.domElement.current, e)}
      >
        <div className={'tp-custom-popup-abstract tp-batch-tms-popup k-hbox k-vbox'}>
          <div className={'tp-custom-popup-header k-hbox k-align-items-center k-justify-content-between'}>
            <Container
              htmlTag={'h3'}
              className='tp-text-semibold tp-font-size tp-custom-popup-header-title'>
              {this.translate('tm')}
            </Container>
          </div>
          <div className={`tp-custom-popup-body ${this.state.tms.length > 6 ? 'tp-has-scroll' : ''}`}>
            {this.state.tms.map((item: TMS_UI, index: number) => {
              const tmProfiles = item.tmProfiles.filter((tmProfile: any) => this.state.targetFormat === tmProfile.targetFormat);
              const sortedTms = orderBy(tmProfiles, (item: any) => item.tmName.toLocaleLowerCase());
              const value = this.setTmProfileValue(item);

              return (
                <div
                  key={item.targetLanguage + index}
                  className={'tp-tm-language-item k-hbox k-align-items-center'}
                >
                  <span
                    aria-hidden={sortedTms.length ? 'true' : 'false'}
                    className='tp-lang-label tp-text-medium tp-font-size-sm k-hbox k-justify-content-end k-text-right'>
                    {item.targetLanguageDisplayName}
                  </span>
                  {sortedTms.length ? (
                    <FilterCombo
                      label={this.translate("tmLanguage", { language: item.targetLanguageDisplayName })}
                      labelClassName={'sr-only'}
                      disabled={this.props.submissionStarted || (sortedTms.length === 1 && sortedTms[0].isDefault)}
                      id={`${this.props.moduleId}-${item.targetLanguage}`}
                      data={sortedTms}
                      textField='tmName'
                      className={`${value?.isDefault ? ' tp-default-tm' : ''}`}
                      size={'small'}
                      itemRender={this.tmItemRenderer}
                      value={value}
                      translateMethod={this.translate.bind(this)}
                      onBlur={this.handleBlur}
                      onFocus={this.handleFocus}
                      onChange={(e: any) => this.onTmChange(e, item)}
                      valueAsSimpleType={true}
                    />
                  ) : (
                    <HBox flex1 className={'pd-no-translation tp-font-size-sm tp-text-semibold'}>
                      {this.translate('noTM')}
                    </HBox>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    );
  }
}

export default FocusControl(TranslationMemories);
